import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  ExpandMore,
  Science,
  Psychology,
  DataUsage,
  Code,
  TrendingUp,
  School,
} from '@mui/icons-material';
import profileConfig from '../constants/profileConfig';

const Research = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const methodologyIcons = {
    'Deep Learning': <DataUsage />,
    'Transfer Learning': <TrendingUp />,
    'Attention Mechanisms': <Psychology />,
    'Federated Learning': <Code />,
    'Computer Vision': <Science />,
    'Natural Language Processing': <Code />,
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Current Research
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Advancing Chemistry for Healthcare and Diabetes Research
            </Typography>
          </motion.div>

          {/* Research Overview */}
          <motion.div variants={itemVariants}>
            <Card sx={{ mb: 6, p: 4 }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                {profileConfig.research.focus}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  mb: 4,
                }}
              >
                {profileConfig.research.description}
              </Typography>
            </Card>
          </motion.div>

          {/* Research Areas */}
          <motion.div variants={itemVariants}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              Research Areas
            </Typography>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              {(profileConfig.research.topics || []).map((topic, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                      },
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          color: 'white',
                        }}
                      >
                        <Science />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                        {topic}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Methodologies */}
          <motion.div variants={itemVariants}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              Research Methodologies
            </Typography>
            <Paper sx={{ p: 4, mb: 6 }}>
              <Grid container spacing={2}>
                {(profileConfig.research?.methodologies || []).map((method, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Chip
                      label={method || '-'}
                      variant="outlined"
                      color="primary"
                      sx={{
                        p: 2,
                        height: 'auto',
                        '& .MuiChip-label': {
                          fontSize: '1rem',
                          fontWeight: 500,
                        },
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </motion.div>

          {/* Detailed Research Projects */}
          <motion.div variants={itemVariants}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              Ongoing Research Projects
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Multimodal Learning for Healthcare Applications
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This project focuses on developing advanced multimodal learning frameworks that can 
                      process and analyze both medical images and clinical text data simultaneously. 
                      The goal is to improve diagnostic accuracy and provide more comprehensive 
                      healthcare solutions.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> In Progress • <strong>Timeline:</strong> 2023-2025
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Explainable AI for Medical Decision Support
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Developing interpretable AI models that can explain their decision-making processes 
                      to healthcare professionals. This research aims to increase trust in AI systems 
                      and improve their adoption in clinical settings.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> In Progress • <strong>Timeline:</strong> 2023-2024
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Federated Learning for Privacy-Preserving Healthcare
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Exploring federated learning approaches to enable collaborative AI model training 
                      across multiple healthcare institutions while preserving patient privacy and 
                      data security.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> Planning Phase • <strong>Timeline:</strong> 2024-2026
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </motion.div>

          {/* Research Impact */}
          <motion.div variants={itemVariants}>
            <Card sx={{ mt: 6, p: 4, backgroundColor: 'primary.main', color: 'white' }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                Research Impact
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      15+
                    </Typography>
                    <Typography variant="body1">
                      Published Papers
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      500+
                    </Typography>
                    <Typography variant="body1">
                      Citations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      10+
                    </Typography>
                    <Typography variant="body1">
                      Research Collaborations
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 6,
                p: 4,
                backgroundColor: 'background.paper',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Interested in Research Collaboration?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                I'm always looking for talented researchers and students to collaborate with. 
                Whether you're interested in joining my research team or exploring potential 
                partnerships, I'd love to discuss opportunities.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Areas of interest: PhD supervision, postdoctoral positions, research partnerships, 
                and collaborative projects in AI and healthcare.
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Research; 